import { useEffect, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import Button from '@components/ui/Button'
import Input from '@components/ui/Input'
import Modal from '@components/ui/Modal'
import Badge from '@components/ui/Badge'
import EmptyState from '@components/ui/EmptyState'
import Loading from '@components/ui/Loading'
import { Search, X, Plus, Pencil, Power, PowerOff, Trash2, ArrowUpDown } from 'lucide-react'
import { listRules, createRule, updateRule, deleteRule } from '@services/ruleService'
import { getDistincts, getDimensionValues } from '@services/payrollService'
import { useToast } from '@hooks/useToast'

export default function RulesPage() {
  const toast = useToast()
  const [rules, setRules] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const emptyForm = useMemo(
    () => ({
      name: '',
      description: '',
      increase_type: 'percentage',
      increase_value: 0,
      target_group: '',
      target_position: '',
      is_active: true,
      priority: 0,
      calculation_base: 'gross',
      conditions: [],
      enable_parity: false,
      parity_situations: [],
    }),
    []
  )
  const [form, setForm] = useState(emptyForm)
  const [distincts, setDistincts] = useState({
    grupos: [],
    cargos: [],
    fundos: [],
    regimes: [],
    eventos: [],
    descricoes_evento: [],
    grupos_evento: [],
    situacoes: [],
  })
  const valuedDims = ['grupo', 'cargo', 'fundo', 'regime', 'desc_evento', 'grupo_evento', 'situacao']

  const ensureDimension = async (dim) => {
    if (!valuedDims.includes(dim)) return
    // Se já temos dados suficientes, não busca
    const has = (arr) => Array.isArray(arr) && arr.length > 0
    const mapState = {
      grupo: distincts.grupos,
      cargo: distincts.cargos,
      fundo: distincts.fundos,
      regime: distincts.regimes,
      evento: distincts.eventos,
      desc_evento: distincts.descricoes_evento,
      grupo_evento: distincts.grupos_evento,
      situacao: distincts.situacoes,
    }
    if (has(mapState[dim])) return
    try {
      const data = await getDimensionValues(dim)
      const results = Array.isArray(data?.results) ? data.results : data
      const values = results.map((r) => r.value)
      setDistincts((prev) => ({
        ...prev,
        grupos: dim === 'grupo' ? values : prev.grupos,
        cargos: dim === 'cargo' ? values : prev.cargos,
        fundos: dim === 'fundo' ? values : prev.fundos,
        regimes: dim === 'regime' ? values : prev.regimes,
        eventos: dim === 'evento' ? values : prev.eventos,
        descricoes_evento: dim === 'desc_evento' ? values : prev.descricoes_evento,
        grupos_evento: dim === 'grupo_evento' ? values : prev.grupos_evento,
        situacoes: dim === 'situacao' ? values : prev.situacoes,
      }))
    } catch (e) {
      // silencioso; UI ainda permite digitar manualmente
    }
  }

  useEffect(() => {
    const sig = form.conditions.map((c) => c.dimension).join('|')
    const unique = Array.from(new Set(form.conditions.map((c) => c.dimension))).filter((d) => valuedDims.includes(d))
    unique.forEach((d) => { ensureDimension(d) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.conditions.map((c) => c.dimension).join('|')])
    const [searchQuery, setSearchQuery] = useState('')
    const [statusFilter, setStatusFilter] = useState('all') // all|active|inactive
    const [sortKey, setSortKey] = useState('priority') // priority|name|updated
    const [sortDir, setSortDir] = useState('asc') // asc|desc
    const [mode, setMode] = useState('create') // create|edit
    const [editingId, setEditingId] = useState(null)

  const fieldClass =
    'w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary'
  const smallFieldClass =
    'w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary'
  const labelClass = 'block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1'
  const hintClass = 'text-xs text-gray-500 dark:text-gray-400'
  const pillClass = (active) =>
    `px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
      active
        ? 'bg-primary text-white border-primary shadow-sm'
        : 'bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-primary/60'
    }`

  const load = async () => {
    const abort = new AbortController()
    try {
      setLoading(true)
      const [data, ds] = await Promise.all([
        listRules({}, abort.signal),
        getDistincts({ limit: 5000, use_cache: 1 }, abort.signal),
      ])
      setRules(data)
      setDistincts(ds)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const filteredRules = useMemo(() => {
    let data = Array.isArray(rules) ? [...rules] : []
    if (statusFilter !== 'all') {
      const want = statusFilter === 'active'
      data = data.filter((r) => !!r.is_active === want)
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      data = data.filter((r) => {
        const name = (r.name || '').toLowerCase()
        const desc = (r.description || '').toLowerCase()
        const group = (r.target_group || '').toLowerCase()
        const pos = (r.target_position || '').toLowerCase()
        return name.includes(q) || desc.includes(q) || group.includes(q) || pos.includes(q)
      })
    }
    const key = sortKey
    data.sort((a, b) => {
      let av, bv
      if (key === 'priority') { av = Number(a.priority); bv = Number(b.priority) }
      else if (key === 'name') { av = (a.name || '').toLowerCase(); bv = (b.name || '').toLowerCase() }
      else { av = new Date(a.updated_at || a.created_at || 0).getTime(); bv = new Date(b.updated_at || b.created_at || 0).getTime() }
      if (av < bv) return sortDir === 'asc' ? -1 : 1
      if (av > bv) return sortDir === 'asc' ? 1 : -1
      return 0
    })
    return data
  }, [rules, statusFilter, searchQuery, sortKey, sortDir])

  const openModal = () => {
    setMode('create')
    setEditingId(null)
    setForm({ ...emptyForm, priority: getNextPriority() })
    setShowModal(true)
  }

  const duplicatePriority = useMemo(() => {
    const p = Number(form.priority)
    if (Number.isNaN(p)) return false
    return rules.some((r) => Number(r.priority) === p && r.id !== editingId)
  }, [form.priority, rules, editingId])

  const getNextPriority = () => {
    if (rules.length === 0) return 1
    const priorities = rules.map(r => Number(r.priority) || 0)
    return Math.max(...priorities) + 1
  }

  const onSubmit = async () => {
    try {
      if (!form.name.trim() || !form.description.trim()) {
        toast.error('Nome e descricao sao obrigatorios')
        return
      }
      if (duplicatePriority) {
        toast.warning('Ja existe regra com essa prioridade')
        return
      }
      if (form.conditions.length > 20) {
        toast.warning('Maximo de 20 condicoes por regra')
        return
      }
      const payload = {
        name: form.name,
        description: form.description,
        increase_type: form.increase_type,
        increase_value: Number(form.increase_value || 0),
        calculation_base: form.calculation_base,
        is_active: form.is_active,
        priority: Number(form.priority || 0),
        target_group: form.target_group || '',
        target_position: form.target_position || '',
        conditions_json: form.conditions.map((c) => ({
          dimension: c.dimension,
          value: c.value,
          increase_type: c.increase_type,
          increase_value: Number(c.increase_value || 0),
          calculation_base: c.calculation_base || form.calculation_base,
        })),
        enable_parity: form.enable_parity || false,
        parity_situations: Array.isArray(form.parity_situations) ? form.parity_situations : [],
      }
      if (mode === 'edit' && editingId) {
        await updateRule(editingId, payload)
      } else {
        await createRule(payload)
      }
      setShowModal(false)
      setForm({ ...emptyForm })
      setEditingId(null)
      setMode('create')
      load()
      toast.success(mode === 'edit' ? 'Regra atualizada com sucesso' : 'Regra criada com sucesso')
    } catch (e) {
      toast.error('Erro ao criar/atualizar: ' + e.message)
    }
  }

  const onToggleActive = async (rule) => {
    try {
      await updateRule(rule.id, { is_active: !rule.is_active })
      load()
      toast.info(`Regra ${!rule.is_active ? 'ativada' : 'desativada'}`)
    } catch (e) {
      toast.error('Erro ao atualizar: ' + e.message)
    }
  }

  const onDelete = async (rule) => {
    if (!confirm(`Excluir regra "${rule.name}"?`)) return
    try {
      await deleteRule(rule.id)
      load()
      toast.success('Regra excluida')
    } catch (e) {
      toast.error('Erro ao excluir: ' + e.message)
    }
  }

  

  if (loading) return <Loading />
  if (error) return <div className="p-4 text-red-600">Erro: {error}</div>

  return (
    <div className="p-4 space-y-6">
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-semibold">Regras</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">Crie e gerencie regras.</p>
          </div>
          <Button className="flex-shrink-0" onClick={() => { setMode('create'); setEditingId(null); setForm({ ...emptyForm, priority: getNextPriority() }); setShowModal(true) }}>
            <Plus className="w-4 h-4 mr-2" /> <span className="hidden sm:inline">Nova Regra</span><span className="sm:hidden">Nova</span>
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <Input
              label="Buscar"
              placeholder="Nome, descrição..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              LeftIcon={Search}
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSearchQuery('')}
            aria-label="Limpar busca"
            title="Limpar busca"
            className="self-end"
          >
            <X className="w-4 h-4" />
          </Button>

          <div className="hidden md:flex items-center gap-2">
            <span className="text-xs text-gray-500">Status:</span>
            <button className={pillClass(statusFilter==='all')} onClick={()=> setStatusFilter('all')}>Todos</button>
            <button className={pillClass(statusFilter==='active')} onClick={()=> setStatusFilter('active')}>Ativas</button>
            <button className={pillClass(statusFilter==='inactive')} onClick={()=> setStatusFilter('inactive')}>Inativas</button>
          </div>

          <div className="flex items-end gap-2">
            <div className="flex-1 sm:flex-none">
              <label className="label"><span className="label-text text-xs">Ordenar</span></label>
              <select className="select select-bordered select-sm w-full" value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
                <option value="priority">Prioridade</option>
                <option value="name">Nome</option>
                <option value="updated">Atualizado</option>
              </select>
            </div>
            <div className="pb-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSortDir((d)=> d==='asc'?'desc':'asc')}
                title={sortDir==='asc'?'Ascendente':'Descendente'}
                aria-label="Alternar ordem"
              >
                <ArrowUpDown className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        <div className="md:hidden flex items-center gap-2">
          <span className="text-xs text-gray-500">Status:</span>
          <button className={pillClass(statusFilter==='all')} onClick={()=> setStatusFilter('all')}>Todos</button>
          <button className={pillClass(statusFilter==='active')} onClick={()=> setStatusFilter('active')}>Ativas</button>
          <button className={pillClass(statusFilter==='inactive')} onClick={()=> setStatusFilter('inactive')}>Inativas</button>
        </div>
      </div>

      {filteredRules.length === 0 && (
        <EmptyState title="Nenhuma regra" description={searchQuery ? 'Ajuste a busca/filters e tente novamente.' : 'Crie sua primeira regra para começar.'} action={<Button onClick={() => { setMode('create'); setEditingId(null); setForm({ ...emptyForm, priority: getNextPriority() }); setShowModal(true) }}>Nova Regra</Button>} />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filteredRules.map((r) => (
          <motion.div
            key={r.id}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.01 }}
            className="card card-bordered shadow-soft"
          >
            <div className="card-body">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="card-title text-base">{r.name}</h3>
                  <p className="text-xs text-gray-500">{r.description || '-'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${r.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                  {r.is_active ? <Badge variant="success">Ativa</Badge> : <Badge variant="danger">Inativa</Badge>}
                </div>
              </div>
              <div className="mt-2 text-sm">
                <div className="flex items-center gap-4">
                  <span>Tipo: <strong>{r.increase_type === 'percentage' ? 'Percentual' : 'Valor Fixo'}</strong></span>
                  <span>Valor: <strong>{r.increase_type === 'percentage' ? `${r.increase_value}%` : `R$ ${Number(r.increase_value).toLocaleString('pt-BR')}`}</strong></span>
                </div>
                <div className="flex items-center gap-4 mt-1">
                  <span>Prioridade: <strong>{r.priority}</strong></span>
                  <span>Condicoes: <strong>{Array.isArray(r.conditions_json) ? r.conditions_json.length : 0}</strong></span>
                </div>
                {r.enable_parity && (
                  <div className="flex items-center gap-2 mt-2 p-2 rounded bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
                    <span className="text-xs text-purple-700 dark:text-purple-300">
                      ✨ Paridade ativada para{' '}
                      <strong>{Array.isArray(r.parity_situations) && r.parity_situations.length > 0 ? r.parity_situations.join(', ') : 'não configurado'}</strong>
                    </span>
                  </div>
                )}
              </div>
              <div className="card-actions justify-end mt-2">
                <Button variant="ghost" size="sm" aria-label="Editar" title="Editar" onClick={() => {
                  setMode('edit');
                  setEditingId(r.id);
                  setForm({
                    name: r.name || '',
                    description: r.description || '',
                    increase_type: r.increase_type || 'percentage',
                    increase_value: r.increase_value || 0,
                    target_group: r.target_group || '',
                    target_position: r.target_position || '',
                    is_active: !!r.is_active,
                    priority: r.priority || 0,
                    calculation_base: r.calculation_base || 'gross',
                    conditions: Array.isArray(r.conditions_json) ? r.conditions_json.map((c) => ({
                      dimension: c.dimension,
                      value: c.value,
                      increase_type: c.increase_type || 'percentage',
                      increase_value: c.increase_value || 0,
                      calculation_base: c.calculation_base || (r.calculation_base || 'gross'),
                    })) : [],
                    enable_parity: r.enable_parity || false,
                    parity_situations: Array.isArray(r.parity_situations) ? r.parity_situations : [],
                  });
                  setShowModal(true);
                }}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  variant={r.is_active ? 'outline' : 'success'}
                  size="sm"
                  onClick={() => onToggleActive(r)}
                  aria-label={r.is_active ? 'Desativar' : 'Ativar'}
                  title={r.is_active ? 'Desativar' : 'Ativar'}
                >
                  {r.is_active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                </Button>
                <Button variant="danger" size="sm" onClick={() => onDelete(r)} aria-label="Excluir" title="Excluir">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              mode === 'edit' 
                ? 'bg-blue-100 dark:bg-blue-900/30' 
                : 'bg-green-100 dark:bg-green-900/30'
            }`}>
              {mode === 'edit' ? (
                <Pencil className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              ) : (
                <Plus className="w-5 h-5 text-green-600 dark:text-green-400" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {mode === 'edit' ? 'Editar Regra' : 'Nova Regra'}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {mode === 'edit' ? 'Atualize as configurações da regra' : 'Configure uma nova regra'}
              </p>
            </div>
          </div>
        }
        size="xl"
        footer={
          <div className="flex items-center justify-between w-full">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {form.conditions.length > 0 && (
                <span>✓ {form.conditions.length} condição(ões) configurada(s)</span>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={onSubmit} 
                disabled={duplicatePriority || !form.name.trim() || !form.description.trim()}
                className="min-w-[100px]"
              >
                {mode === 'edit' ? 'Atualizar' : 'Criar Regra'}
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-6">
          {/* Cabeçalho com Status e Prioridade */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 pb-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex-1">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                Configure os detalhes da regra
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3 sm:gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600 dark:text-gray-400">Status:</span>
                <button
                  type="button"
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    form.is_active
                      ? 'bg-green-500'
                      : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                  onClick={() => setForm({ ...form, is_active: !form.is_active })}
                  title={form.is_active ? 'Ativa' : 'Inativa'}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                      form.is_active ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600 dark:text-gray-400">Prioridade:</span>
                <input
                  type="number"
                  className="w-16 px-2 py-1 text-xs text-center rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-primary"
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  min="0"
                  title="Números menores executam primeiro"
                />
              </div>
            </div>
          </div>
          {duplicatePriority && (
            <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 -mt-2">
              <p className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1">
                ⚠️ Já existe regra com essa prioridade
              </p>
            </div>
          )}

          {/* Passo 1: Dê um nome para a regra */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold">1</div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Dê um nome para a regra
              </h4>
            </div>
            <div>
              <input
                className={`${fieldClass} text-base`}
                placeholder="Ex.: Aumento 5% para Administrativos"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
              <p className={hintClass}>Um nome claro ajuda a identificar rapidamente a regra.</p>
            </div>
          </div>

          {/* Passo 2: Descreva o objetivo */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold">2</div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Descreva o objetivo desta regra
              </h4>
            </div>
            <div>
              <textarea
                className={`${fieldClass} min-h-[80px] resize-none`}
                placeholder="Ex.: Aplicar reajuste anual de 5% aos colaboradores do grupo Administrativo..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                required
              />
              <p className={hintClass}>Explique quando e por que esta regra deve ser aplicada.</p>
            </div>
          </div>

          {/* Passo 3: Configuração de Aumento */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold">3</div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Configuração de Aumento
              </h4>
            </div>

            <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50/50 dark:bg-gray-900/30">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                {/* Tipo */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                    Tipo <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-1.5">
                    <button
                      type="button"
                      className={`${pillClass(form.increase_type === 'percentage')} h-10 justify-center text-xs w-full`}
                      onClick={() => setForm({ ...form, increase_type: 'percentage' })}
                    >
                      % Perc.
                    </button>
                    <button
                      type="button"
                      className={`${pillClass(form.increase_type === 'fixed')} h-10 justify-center text-xs w-full`}
                      onClick={() => setForm({ ...form, increase_type: 'fixed' })}
                    >
                      R$ Fixo
                    </button>
                  </div>
                </div>

                {/* Valor */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                    Valor <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 text-sm font-semibold">
                      {form.increase_type === 'percentage' ? '%' : 'R$'}
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className={`${fieldClass} pl-10 pr-3 h-10 text-base font-semibold`}
                      placeholder={form.increase_type === 'percentage' ? '5.00' : '1200.00'}
                      value={form.increase_value}
                      onChange={(e) => setForm({ ...form, increase_value: e.target.value })}
                    />
                  </div>
                </div>

                {/* Base de Cálculo */}
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                    Base de Cálculo <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-1.5">
                    <button
                      type="button"
                      className={`${pillClass(form.calculation_base === 'gross')} h-10 justify-center text-xs w-full`}
                      onClick={() => setForm({ ...form, calculation_base: 'gross' })}
                    >
                      Salário Bruto
                    </button>
                    <button
                      type="button"
                      className={`${pillClass(form.calculation_base === 'net')} h-10 justify-center text-xs w-full`}
                      onClick={() => setForm({ ...form, calculation_base: 'net' })}
                    >
                      Salário Líquido
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Passo 4: Paridade */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold">4</div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Paridade (Opcional)
              </h4>
            </div>

            <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-purple-50/50 to-white dark:from-purple-950/20 dark:to-gray-800/60">
              <div className="flex items-start gap-3 mb-4">
                <div className="flex-1">
                  <p className={hintClass}>
                    ⚖️ <strong>Paridade:</strong> Quando ativada, o concedido da regra será replicado integralmente (100%) para as situações selecionadas (aposentados, pensionistas, etc.).
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-600 dark:text-gray-400">Ativar Paridade:</span>
                  <button
                    type="button"
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      form.enable_parity
                        ? 'bg-purple-500'
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                    onClick={() => setForm({ ...form, enable_parity: !form.enable_parity })}
                    title={form.enable_parity ? 'Paridade ativada' : 'Paridade desativada'}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                        form.enable_parity ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {form.enable_parity && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                    Situações que Receberão Paridade <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {distincts.situacoes.map((sit) => (
                      <label
                        key={sit}
                        className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          form.parity_situations.includes(sit)
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30'
                            : 'border-gray-300 dark:border-gray-600 hover:border-purple-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="checkbox checkbox-sm checkbox-primary"
                          checked={form.parity_situations.includes(sit)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setForm({ ...form, parity_situations: [...form.parity_situations, sit] })
                            } else {
                              setForm({ ...form, parity_situations: form.parity_situations.filter(s => s !== sit) })
                            }
                          }}
                        />
                        <span className="text-sm font-medium">{sit}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    ✓ Marque as situações que receberão paridade integral (100% do aumento)
                  </p>
                </div>
              )}

              {!form.enable_parity && (
                <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                  <p className="text-sm">Paridade desativada - Apenas os ativos vão ser afetado</p>
                </div>
              )}
            </div>
          </div>

          {/* Passo 5: Condições */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold">5</div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Condições
                </h4>
              </div>
              <Badge variant={form.conditions.length >= 20 ? 'danger' : 'secondary'} className="text-xs">
                {form.conditions.length}/20
              </Badge>
            </div>

            <div className="border-2 border-gray-200 dark:border-gray-700 rounded-xl p-5 bg-gradient-to-br from-gray-50 to-white dark:from-gray-900/60 dark:to-gray-800/60">
              <div className="flex items-center justify-between mb-4">
                <div className="flex-1">
                  <p className={hintClass}>
                    Adicione condições para segmentar por grupo, cargo, evento, fundo, regime ou situação.
                  </p>
                </div>
              </div>

              {/* Adicionar Condição Rápida */}
              <div className="mb-4 p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                <p className="text-xs font-bold text-blue-900 dark:text-blue-300 uppercase tracking-wide mb-3">
                  ⚡ Adicionar Condição Rápida
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <select 
                    className={smallFieldClass} 
                    value={form.__quick_dim || ''} 
                    onChange={(e)=> setForm({ ...form, __quick_dim: e.target.value, __quick_val: '' })}
                  >
                    <option value="">Selecione a dimensão...</option>
                    <option value="grupo">👥 Grupo</option>
                    <option value="cargo">💼 Cargo</option>
                    <option value="fundo">🏦 Fundo</option>
                    <option value="regime">📋 Regime</option>
                    <option value="grupo_evento">📊 Grupo do Evento</option>
                    <option value="situacao">📍 Situação</option>
                    <option value="desc_evento">📝 Evento</option>
                  </select>
                  <select 
                    className={smallFieldClass} 
                    value={form.__quick_val || ''} 
                    onChange={(e)=> setForm({ ...form, __quick_val: e.target.value })}
                    disabled={!form.__quick_dim}
                  >
                    <option value="">Selecione o valor...</option>
                    {(form.__quick_dim === 'grupo' ? distincts.grupos
                      : form.__quick_dim === 'cargo' ? distincts.cargos
                      : form.__quick_dim === 'fundo' ? distincts.fundos
                      : form.__quick_dim === 'regime' ? distincts.regimes
                      : form.__quick_dim === 'grupo_evento' ? distincts.grupos_evento
                      : form.__quick_dim === 'situacao' ? distincts.situacoes
                      : distincts.descricoes_evento).map(v => (
                        <option key={String(v)} value={String(v)}>{String(v)}</option>
                      ))}
                  </select>
                  <Button 
                    size="sm" 
                    disabled={!form.__quick_dim || !form.__quick_val || form.conditions.length >= 20}
                    onClick={() => {
                      if (!form.__quick_dim || !form.__quick_val) return
                      setForm({
                        ...form,
                        conditions: [...form.conditions, { 
                          dimension: form.__quick_dim, 
                          value: form.__quick_val, 
                          increase_type: form.increase_type, 
                          increase_value: form.increase_value, 
                          calculation_base: form.calculation_base 
                        }],
                        __quick_dim: '',
                        __quick_val: '',
                      })
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Adicionar
                  </Button>
                </div>
              </div>

              {form.conditions.length === 0 ? (
                <div className="text-center py-8 px-4 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    Nenhuma condição configurada
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    Use o atalho acima ou clique em "Adicionar Condição Manual" para criar condições específicas
                  </p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                  {form.conditions.map((c, idx) => {
                    const options =
                      c.dimension === 'grupo' ? distincts.grupos
                      : c.dimension === 'cargo' ? distincts.cargos
                      : c.dimension === 'fundo' ? distincts.fundos
                      : c.dimension === 'regime' ? distincts.regimes
                      : c.dimension === 'grupo_evento' ? distincts.grupos_evento
                      : c.dimension === 'situacao' ? distincts.situacoes
                      : distincts.descricoes_evento
                    
                    return (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <Badge variant="primary" className="text-xs">
                            Condição #{idx + 1}
                          </Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                            onClick={() =>
                              setForm({
                                ...form,
                                conditions: form.conditions.filter((_, i) => i !== idx),
                              })
                            }
                          >
                            <Trash2 className="w-4 h-4 mr-1" /> Remover
                          </Button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                              Dimensão
                            </label>
                            <select
                              className={smallFieldClass}
                              value={c.dimension}
                              onChange={(e) =>
                                setForm({
                                  ...form,
                                  conditions: form.conditions.map((it, i) =>
                                    i === idx ? { ...it, dimension: e.target.value, value: '' } : it
                                  ),
                                })
                              }
                            >
                              <option value="grupo">Grupo</option>
                              <option value="cargo">Cargo</option>
                              <option value="fundo">Fundo</option>
                              <option value="regime">Regime</option>
                              <option value="desc_evento">Evento</option>
                              <option value="grupo_evento">Grupo Evento</option>
                              <option value="situacao">Situação</option>
                            </select>
                          </div>

                          <div className="md:col-span-2">
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                              Valor
                            </label>
                            {valuedDims.includes(c.dimension) ? (
                              <select
                                className={smallFieldClass}
                                value={c.value}
                                onChange={(e) =>
                                  setForm({
                                    ...form,
                                    conditions: form.conditions.map((it, i) =>
                                      i === idx ? { ...it, value: e.target.value } : it
                                    ),
                                  })
                                }
                              >
                                <option value="">Selecione...</option>
                                {Array.isArray(options) && options.length > 0 ? (
                                  options.map((opt) => (
                                    <option key={String(opt)} value={String(opt)}>
                                      {String(opt)}
                                    </option>
                                  ))
                                ) : (
                                  <option value="" disabled>(carregando...)</option>
                                )}
                              </select>
                            ) : (
                              <input
                                className={smallFieldClass}
                                value={c.value}
                                placeholder="Digite o valor"
                                onChange={(e) =>
                                  setForm({
                                    ...form,
                                    conditions: form.conditions.map((it, i) =>
                                      i === idx ? { ...it, value: e.target.value } : it
                                    ),
                                  })
                                }
                              />
                            )}
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                              Tipo
                            </label>
                            <select
                              className={smallFieldClass}
                              value={c.increase_type}
                              onChange={(e) =>
                                setForm({
                                  ...form,
                                  conditions: form.conditions.map((it, i) =>
                                    i === idx ? { ...it, increase_type: e.target.value } : it
                                  ),
                                })
                              }
                            >
                              <option value="percentage">%</option>
                              <option value="fixed">R$</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                              Valor
                            </label>
                            <input
                              className={smallFieldClass}
                              type="number"
                              step="0.01"
                              min="0"
                              value={c.increase_value}
                              onChange={(e) =>
                                setForm({
                                  ...form,
                                  conditions: form.conditions.map((it, i) =>
                                    i === idx ? { ...it, increase_value: e.target.value } : it
                                  ),
                                })
                              }
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                              Base
                            </label>
                            <select
                              className={smallFieldClass}
                              value={c.calculation_base || form.calculation_base}
                              onChange={(e) =>
                                setForm({
                                  ...form,
                                  conditions: form.conditions.map((it, i) =>
                                    i === idx ? { ...it, calculation_base: e.target.value } : it
                                  ),
                                })
                              }
                            >
                              <option value="gross">Bruto</option>
                              <option value="net">Líquido</option>
                            </select>
                          </div>
                        </div>
                      </motion.div>
                    )
                  })}
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-center">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={form.conditions.length >= 20}
                  onClick={() =>
                    setForm({
                      ...form,
                      conditions: [
                        ...form.conditions,
                        {
                          dimension: 'grupo',
                          value: '',
                          increase_type: 'percentage',
                          increase_value: 0,
                          calculation_base: form.calculation_base,
                        },
                      ],
                    })
                  }
                  className="min-w-[200px]"
                >
                  <Plus className="w-4 h-4 mr-2" /> Adicionar Condição
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}
